import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { SupersetFrame } from '@growthaxl/web-foundation';
import { PlanCard } from '../cards/PlanCard';
import { ESASparkIcon } from '../panel/ESASparkIcon';
import { MessageAttachmentChip } from './MessageAttachmentChip';
import { BlockRenderer } from '../blocks/BlockRenderer';
import { normalizeAgentMarkdown } from '../../utils/normalizeAgentMarkdown';
import type { ESACacheMessage } from '../../cache/MessageCache';
import type { Block, MessageAttachmentRef, UiDirectiveEmbedView } from '../../types';
import { OpenCommitmentCard } from './OpenCommitmentCard';
import { readOpenCommitment } from '../../utils/openCommitment';
import {
  filterBlocksForOpenCommitment,
  proseStillContainsOpenCommitmentQuestion,
  stripOpenCommitmentQuestionFromProse,
} from '../../utils/openCommitmentPresentation';
import type { OpenCommitment } from '../../types';

function shouldShowOpenCommitmentQuestionInProse(
  displayText: string,
  commitment: OpenCommitment,
  settledWith?: string,
): boolean {
  if ((settledWith ?? '').trim()) {
    return false;
  }
  const question = (commitment.question ?? '').trim();
  if (!question) {
    return false;
  }
  return !proseStillContainsOpenCommitmentQuestion(displayText, commitment);
}

/** Bold owned ask in the message bubble — options render in OpenCommitmentCard. */
function OpenCommitmentQuestionProse({
  question,
  className = '',
}: {
  question: string;
  className?: string;
}): React.ReactElement {
  return (
    <p className={`text-[14px] leading-relaxed text-text-primary ${className}`.trim()}>
      <strong className="font-semibold">{question}</strong>
    </p>
  );
}

/**
 * Character threshold above which a plain-text message shows the expand icon.
 * ~300 chars ≈ 3–4 short sentences — long enough to benefit from a larger reading surface.
 */
const EXPAND_THRESHOLD = 300;

/**
 * Expand / maximize icon — shown on long text message bubbles to open the
 * canvas-area overlay for comfortable reading.
 */
function ExpandIcon(): React.ReactElement {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 2h4v4M6 14H2v-4M14 2l-6 6M2 14l6-6" />
    </svg>
  );
}

/** Reads structured response blocks from the message payload (PRD-050).
 * Present on both 'text' (direct_answer) and 'json' messageType messages —
 * checked ahead of the legacy response_type switch below (DR-11/AC-6).
 */
function getBlocks(message: ESACacheMessage): Block[] | null {
  const blocks = message.message.blocks;
  return Array.isArray(blocks) && blocks.length > 0 ? (blocks as Block[]) : null;
}

const PLAN_SAMPLE_MARKDOWN_RE =
  /\n*Here's a small sample of the data in scope:\n+(?:\|[^\n]*\n)+(?:\n*_Sample of[^\n]*_\n*)?/gi;

const ARTEFACT_LINE_RE = /^(?:- .+|(?:dashboard|card|table|chart)\b.*)$/i;

/** Drop prompt-only UI context blocks that leaked into chat copy. */
function stripOnScreenUiContext(text: string): string {
  const marker = text.search(/\[On-screen UI context/i);
  if (marker < 0) return text;

  let start = marker;
  while (start > 0 && (text[start - 1] === ' ' || text[start - 1] === '\t')) {
    start -= 1;
  }
  if (start > 0 && text[start - 1] === '\n') {
    start -= 1;
    if (start > 0 && text[start - 1] === '\n') {
      start -= 1;
    }
  }

  const remainder = text.slice(marker);
  const lines = remainder.split(/(?<=\n)/);
  let consumed = lines[0]?.length ?? 0;
  for (let i = 1; i < lines.length; i += 1) {
    const line = lines[i] ?? '';
    const stripped = line.trim();
    if (!stripped) {
      consumed += line.length;
      continue;
    }
    if (ARTEFACT_LINE_RE.test(stripped)) {
      consumed += line.length;
      continue;
    }
    break;
  }
  const cleaned = `${text.slice(0, start)}\n\n${text.slice(marker + consumed)}`;
  return cleaned.replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * ADR-0023: ``message`` stays authoritative; ``blocks`` are additive.
 * Show prose above blocks when blocks do not already carry a paragraph
 * (avoids duplicating LLM ontology answers that mirror prose in a paragraph block).
 * Plan sample turns emit metric/table only — without this, "Should I proceed?" disappears.
 */
function proseAlongsideBlocks(text: string, blocks: Block[]): string | null {
  let prose = text.trim();
  if (!prose) return null;
  if (blocks.some(block => block.type === 'paragraph')) return null;
  // Display-time cleanup for plan cards (and stale cached turns): never show
  // prompt-only UI context or a markdown sample when metric/table blocks exist.
  prose = stripOnScreenUiContext(prose);
  if (blocks.some(block => block.type === 'table' || block.type === 'metric')) {
    prose = prose.replace(PLAN_SAMPLE_MARKDOWN_RE, '\n\n');
  }
  prose = prose.replace(/\n{3,}/g, '\n\n').trim();
  return prose || null;
}

/**
 * Reads the structured ui_directives from the message payload.
 * Returns the directive only when type is 'embed_view' and a URL is present.
 */
function getEmbedDirective(message: ESACacheMessage): UiDirectiveEmbedView | null {
  const d = message.message.ui_directives as UiDirectiveEmbedView | undefined;
  if (d?.type === 'embed_view' && d.embed_view_url) return d;
  return null;
}

export interface ESAMessageBubbleProps {
  message: ESACacheMessage;
  className?: string;
  /** PRD-050: forwarded to BlockRenderer for follow_up_chips clicks. */
  onFollowUpSelect?: (suggestion: string) => void;
  /** PRD-050: forwarded to BlockRenderer for approval_card option clicks. */
  onApprovalOptionSelect?: (option: string, hilRequestId?: string) => void;
  /**
   * DESO open_commitment option click — same send path as follow-ups.
   * Falls back to onFollowUpSelect when omitted.
   */
  onOpenCommitmentSelect?: (option: string) => void;
  /**
   * The reply that already answered this message's open ask, if any. Collapses
   * the options into a record of the decision — see OpenCommitmentCard.
   */
  openCommitmentSettledWith?: string;
  /**
   * Called when the user clicks the expand icon on a long plain-text message.
   * The host opens a canvas-area overlay showing the full message.
   */
  onExpand?: (message: ESACacheMessage) => void;
  /**
   * When true, skips the bubble chrome (avatar, card border, background) and
   * renders only the message content — used by ESAMessageExpandOverlay so the
   * overlay's own surface acts as the reading surface.
   */
  naked?: boolean;
}

/**
 * DR-17 observability hook — logs whenever BlockRenderer falls back to plain
 * text for an unrecognized/malformed block entry, so silent structural drift
 * between backend-emitted blocks and this client's Block union is visible in
 * telemetry/console rather than only as a degraded (but non-crashing) UI.
 */
function logUnknownBlock(block: unknown): void {
  const type =
    block && typeof block === 'object' && 'type' in block
      ? String((block as { type: unknown }).type)
      : 'unknown';
  console.warn('[ESAMessageBubble] structured_block_unknown_type', { type });
}

/** Renders agent markdown safely via react-markdown (no dangerouslySetInnerHTML). */
function TextContent({ text }: { text: string }): React.ReactElement {
  const markdown = normalizeAgentMarkdown(text);
  return (
    <div className="text-[14px] font-normal leading-relaxed text-text-primary space-y-1.5">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          em: ({ children }) => <em className="italic">{children}</em>,
          ul: ({ children }) => <ul className="list-disc pl-4 space-y-0.5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-4 space-y-0.5">{children}</ol>,
          li: ({ children }) => <li className="text-[14px] font-normal">{children}</li>,
          code: ({ children, className }) => {
            const isBlock = Boolean(className);
            return isBlock ? (
              <code className={`font-mono text-xs bg-surface-secondary block p-2 rounded overflow-x-auto ${className ?? ''}`}>
                {children}
              </code>
            ) : (
              <code className="font-mono text-xs bg-surface-secondary px-1 py-0.5 rounded">{children}</code>
            );
          },
          pre: ({ children }) => (
            <pre className="bg-surface-secondary rounded p-2 overflow-x-auto text-xs my-1">{children}</pre>
          ),
          h1: ({ children }) => <h1 className="text-base font-bold mt-2 mb-1">{children}</h1>,
          h2: ({ children }) => <h2 className="text-sm font-bold mt-2 mb-1">{children}</h2>,
          h3: ({ children }) => <h3 className="text-sm font-semibold mt-1.5 mb-0.5">{children}</h3>,
          h4: ({ children }) => <h4 className="text-sm font-semibold">{children}</h4>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-border-default pl-3 italic text-text-secondary">
              {children}
            </blockquote>
          ),
          a: ({ href, children }) => (
            <a href={href} className="text-brand-primary underline" target="_blank" rel="noreferrer">
              {children}
            </a>
          ),
          // GFM table elements
          table: ({ children }) => (
            <div className="overflow-x-auto my-2">
              <table className="min-w-full border-collapse text-xs">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-surface-secondary">{children}</thead>,
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => <tr className="border-b border-border-default">{children}</tr>,
          th: ({ children }) => (
            <th className="border border-border-default px-2 py-1.5 text-left font-semibold whitespace-nowrap">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-border-default px-2 py-1.5 align-top">{children}</td>
          ),
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}

interface BadgeItem {
  label: string;
  count: number;
  /** Optional Tailwind bg-* class for the dot colour, defaults to bg-brand-primary. */
  color?: string;
}

/**
 * Renders a text body followed by a row of pill badges — used for agent
 * responses that surface countable action-items (e.g. "9 Agent requests").
 * Matches the EDB v2 reference design: blue-dot + count + label inside a
 * rounded-full border pill.
 */
function ItemBadges({ text, items }: { text: string; items: BadgeItem[] }): React.ReactElement {
  return (
    <div>
      <TextContent text={text} />
      <div className="flex flex-wrap gap-2 mt-2.5">
        {items.map((item, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-border-default bg-surface text-xs"
          >
            <span
              className={`w-2 h-2 rounded-full shrink-0${item.color ? ` ${item.color}` : ' bg-brand-primary'}`}
            />
            <span className="font-semibold text-text-primary">{item.count}</span>
            <span className="text-text-secondary">{item.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function AgentContent({ message }: { message: ESACacheMessage }): React.ReactElement {
  const payload = message.message as Record<string, unknown>;
  const responseType = payload.response_type as string | undefined;
  const text = typeof payload.text === 'string' ? payload.text : '';

  if (responseType === 'plan_presentation' && Array.isArray(payload.steps)) {
    return (
      <PlanCard
        title={text}
        steps={(payload.steps as Array<{ id: string; title: string; status?: 'pending' | 'running' | 'completed' | 'failed' }>)}
      />
    );
  }

  // Stage updates read as normal assistant replies (not TaskCards).
  // Chat gets a fuller sentence; inbox notifications keep short title/summary.
  if (
    responseType === 'task_progress' ||
    responseType === 'task_completed' ||
    responseType === 'task_failed'
  ) {
    return (
      <TextContent text={relayConversationReply(payload, text, responseType)} />
    );
  }

  if (responseType === 'task_dispatched' && typeof payload.task_id === 'string') {
    return (
      <TextContent text={relayConversationReply(payload, text, responseType)} />
    );
  }

  // Generic structured items → badge pills (e.g. attention summary with counts)
  if (Array.isArray(payload.items) && (payload.items as unknown[]).length > 0) {
    return (
      <ItemBadges
        text={text || JSON.stringify(payload)}
        items={payload.items as BadgeItem[]}
      />
    );
  }

  return <TextContent text={text || JSON.stringify(payload)} />;
}

/**
 * Conversational chat copy for relay stage updates.
 * Notifications use title=headline; chat shows the backend description when present
 * (per-stage agent summary or terminal combined/error). No stock frontend framing.
 */
function relayConversationReply(
  payload: Record<string, unknown>,
  text: string,
  _responseType?: string,
): string {
  const description =
    typeof payload.description === 'string' ? payload.description.trim() : '';
  if (description) {
    return description;
  }
  const headline = (text || '').trim();
  return headline.replace(/^completed:\s*/i, '').trim() || headline;
}

/**
 * Extracts attachment refs from a cache message.
 * Supports the in-memory array stored in message.attachments (optimistic sends
 * and enriched agent responses) and the single stored_file_ukey from server history.
 */
function getAttachments(message: ESACacheMessage): MessageAttachmentRef[] {
  if (Array.isArray(message.message.attachments) && message.message.attachments.length > 0) {
    return message.message.attachments as MessageAttachmentRef[];
  }
  if (message.hasAttachments && message.storedFileUkey) {
    return [{ stored_file_ukey: message.storedFileUkey }];
  }
  return [];
}

/**
 * Horizontal strip of read-only attachment chips shown beneath message text.
 */
function AttachmentRow({
  attachments,
  variant,
}: {
  attachments: MessageAttachmentRef[];
  variant: 'user' | 'agent';
}): React.ReactElement | null {
  if (attachments.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {attachments.map(att => (
        <MessageAttachmentChip
          key={att.stored_file_ukey}
          stored_file_ukey={att.stored_file_ukey}
          file_name={att.file_name}
          content_type={att.content_type}
          variant={variant}
        />
      ))}
    </div>
  );
}

function MessageTimestamp({
  iso,
  align = 'start',
}: {
  iso?: string;
  align?: 'start' | 'end';
}): React.ReactElement | null {
  if (!iso) return null;
  const date = new Date(iso);
  const label = date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  return (
    <time
      dateTime={iso}
      className={`mt-1 block text-[11px] leading-none text-text-tertiary ${
        align === 'end' ? 'text-right' : 'text-left'
      }`}
    >
      {label}
    </time>
  );
}

/**
 * Single message row.
 *
 * User   → right-aligned blue bubble with top-right tail (12px 4px 12px 12px)
 * Agent  → ESASparkIcon avatar left + white card taking full remaining width
 */
export function ESAMessageBubble({
  message,
  className,
  onFollowUpSelect,
  onApprovalOptionSelect,
  onOpenCommitmentSelect,
  openCommitmentSettledWith,
  onExpand,
  naked = false,
}: ESAMessageBubbleProps): React.ReactElement {
  const isUser = message.initiator === 'user';
  const attachments = getAttachments(message);
  const handleOptionSelect = onOpenCommitmentSelect ?? onFollowUpSelect;

  if (isUser) {
    // message.message may arrive as a bare string from server history (backend stores
    // user text as a plain string, not { text }).  mapApiMessage normalises it, but
    // guard here too so any cache-bypass path never renders JSON-quoted text.
    const rawMsg = message.message as unknown;
    const userText =
      typeof rawMsg === 'string'
        ? rawMsg
        : typeof message.message.text === 'string'
          ? message.message.text
          : JSON.stringify(message.message);

    return (
      <div
        className={`flex justify-end ${className ?? ''}`}
        data-message-id={message.id}
      >
        {/*
         * User bubble: top-right 4px tail mirrors agent bubble (top-left tail).
         * Radius: 12px 4px 12px 12px — matches the EDB v2 chat tail convention.
         * bg-edb-accent + text-brand-on-primary are DS tokens.
         */}
        <div className="flex max-w-[78%] flex-col items-end">
          <div className="rounded-[12px_4px_12px_12px] px-[13px] py-[9px] text-[13px] leading-relaxed bg-edb-accent text-brand-on-primary">
            {userText}
            <AttachmentRow attachments={attachments} variant="user" />
          </div>
          <MessageTimestamp iso={message.createdAt} align="end" />
        </div>
      </div>
    );
  }

  const agentText = typeof message.message.text === 'string'
    ? message.message.text
    : JSON.stringify(message.message);

  const embedDirective = getEmbedDirective(message);
  const rawBlocks = getBlocks(message);
  const openCommitment = readOpenCommitment(message);
  const displayAgentText = openCommitment
    ? stripOpenCommitmentQuestionFromProse(agentText, openCommitment)
    : agentText;
  const displayBlocks = openCommitment
    ? filterBlocksForOpenCommitment(rawBlocks, openCommitment)
    : rawBlocks;

  // Expand icon is shown only for plain text messages (no blocks, messageType === 'text')
  // that exceed the character threshold — structured/json messages are not expandable.
  const agentExpandable =
    onExpand !== undefined &&
    displayBlocks === null &&
    displayAgentText.length >= EXPAND_THRESHOLD;

  const blocksProse = displayBlocks
    ? proseAlongsideBlocks(displayAgentText, displayBlocks)
    : null;
  const showOpenCommitmentQuestionInProse = openCommitment
    ? shouldShowOpenCommitmentQuestionInProse(
        displayAgentText,
        openCommitment,
        openCommitmentSettledWith,
      )
    : false;
  const hideOpenCommitmentQuestion =
    !openCommitment ||
    showOpenCommitmentQuestionInProse ||
    !openCommitment.question?.trim() ||
    proseStillContainsOpenCommitmentQuestion(displayAgentText, openCommitment);
  const openCommitmentNode =
    openCommitment && handleOptionSelect ? (
      <OpenCommitmentCard
        commitment={openCommitment}
        onSelect={handleOptionSelect}
        settledWith={openCommitmentSettledWith}
        hideQuestion={hideOpenCommitmentQuestion}
      />
    ) : null;

  const openCommitmentQuestionNode =
    openCommitment && showOpenCommitmentQuestionInProse ? (
      <OpenCommitmentQuestionProse
        question={(openCommitment.question ?? '').trim()}
        className={displayAgentText || blocksProse ? 'mt-1.5' : ''}
      />
    ) : null;

  // Naked mode: content only — no avatar, no card border/background.
  // Used by ESAMessageExpandOverlay so the overlay surface acts as the reading area.
  if (naked) {
    return (
      <div className={`text-[14px] font-normal leading-relaxed text-text-primary ${className ?? ''}`}>
        {displayBlocks ? (
          <div className="flex flex-col gap-2">
            {blocksProse ? <TextContent text={blocksProse} /> : null}
            <BlockRenderer
              blocks={displayBlocks}
              onFollowUpSelect={onFollowUpSelect}
              onApprovalOptionSelect={onApprovalOptionSelect}
              onUnknownBlock={logUnknownBlock}
            />
          </div>
        ) : message.messageType === 'json' ? (
          <AgentContent message={message} />
        ) : displayAgentText ? (
          <TextContent text={displayAgentText} />
        ) : null}
        {openCommitmentQuestionNode}
        {openCommitmentNode}
        {embedDirective && (
          <div className="mt-3 overflow-hidden rounded-lg">
            <SupersetFrame config={{ src: embedDirective.embed_view_url, dashboard: embedDirective.title }} />
          </div>
        )}
        <AttachmentRow attachments={attachments} variant="agent" />
      </div>
    );
  }

  return (
    <div
      className={`flex gap-2 items-start ${className ?? ''}`}
      data-message-id={message.id}
    >
      {/* ESA spark avatar — 22×22, margin-top 2px to align with first text line */}
      <div className="shrink-0 mt-[2px]">
        <ESASparkIcon size={22} radius={11} />
      </div>

      {/*
       * Agent column: card + timestamp below (outside the card), matching user
       * bubbles where time sits under—not inside—the message surface.
       */}
      <div className="flex min-w-0 flex-1 flex-col items-start">
        {/*
         * Agent card: top-left 4px tail, rest 12px. bg-surface + border-border-default.
         * flex-1 width via parent so the card fills remaining width.
         */}
        <div className="relative w-full min-w-0 bg-surface border border-border-default rounded-[4px_12px_12px_12px] px-[13px] py-[9px] text-[14px] font-normal leading-relaxed text-text-primary">
          {agentExpandable && (
            <button
              type="button"
              onClick={() => onExpand(message)}
              title="Expand message"
              aria-label="Expand message"
              className="absolute top-[7px] right-[8px] opacity-30 hover:opacity-100 transition-opacity inline-flex items-center justify-center w-[20px] h-[20px] rounded text-text-secondary hover:text-text-primary hover:bg-surface-hover"
            >
              <ExpandIcon />
            </button>
          )}
          {displayBlocks ? (
            // ADR-0023 / PRD-050: blocks are additive structured render; keep
            // authoritative prose when blocks do not already include a paragraph.
            <div className="flex flex-col gap-2">
              {blocksProse ? <TextContent text={blocksProse} /> : null}
              <BlockRenderer
                blocks={displayBlocks}
                onFollowUpSelect={onFollowUpSelect}
                onApprovalOptionSelect={onApprovalOptionSelect}
                onUnknownBlock={logUnknownBlock}
              />
            </div>
          ) : message.messageType === 'json' ? (
            <AgentContent message={message} />
          ) : displayAgentText ? (
            <TextContent text={displayAgentText} />
          ) : null}
          {openCommitmentQuestionNode}
          {openCommitmentNode}
          {embedDirective && (
            <div className="mt-3 -mx-[13px] -mb-[9px] overflow-hidden rounded-b-[12px]">
              <SupersetFrame config={{ src: embedDirective.embed_view_url, dashboard: embedDirective.title }} />
            </div>
          )}
          <AttachmentRow attachments={attachments} variant="agent" />
        </div>
        <MessageTimestamp iso={message.createdAt} align="start" />
      </div>
    </div>
  );
}
